/**
 * Utilitaire de normalisation des erreurs (Axios, fetch, Error natifs, etc.)
 * pour ne JAMAIS afficher à l'utilisateur :
 *   - "[object Object]"
 *   - undefined / null
 *   - Error / AxiosError bruts
 *
 * Usage typique :
 *
 *   import { showError, normalizeError } from "@/lib/errors";
 *   try { ... } catch (e) { showError(e); }   // toast.error propre
 *   const msg = normalizeError(e);            // si besoin d'une string
 *
 * Le détail technique reste logué en console (dev only) pour debug.
 */
import { toast } from "sonner";

/** Messages génériques par code HTTP. */
const STATUS_MESSAGES = {
  400: "La requête est invalide. Merci de vérifier les informations saisies.",
  401: "Votre session a expiré. Merci de vous reconnecter.",
  403: "Vous n'avez pas les droits nécessaires pour effectuer cette action.",
  404: "L'élément demandé est introuvable.",
  408: "Le serveur met trop de temps à répondre. Merci de réessayer.",
  409: "Cette opération entre en conflit avec un élément existant.",
  413: "Le fichier ou la requête est trop volumineux.",
  422: "Certaines informations sont manquantes ou invalides.",
  429: "Trop de tentatives. Merci de patienter quelques instants.",
  500: "Une erreur serveur est survenue. Merci de réessayer.",
  501: "Cette fonctionnalité n'est pas encore disponible ou n'est pas correctement configurée.",
  502: "Service temporairement indisponible (passerelle). Merci de réessayer.",
  503: "Service momentanément indisponible. Merci de réessayer.",
  504: "Le serveur met trop de temps à répondre.",
};

const GENERIC_FALLBACK = "Une erreur est survenue. Merci de réessayer ou de vérifier les informations saisies.";

/**
 * Extrait un message lisible depuis un objet de type FastAPI/Pydantic
 * pour une erreur 422 (`{ detail: [{loc, msg, type}, ...] }`).
 */
function _formatValidationErrors(detail) {
  if (!Array.isArray(detail)) return null;
  const lines = detail
    .map((d) => {
      if (!d || typeof d !== "object") return null;
      const field = Array.isArray(d.loc) ? d.loc.filter((p) => p !== "body" && p !== "query" && p !== "path").join(".") : null;
      const msg = d.msg || d.message || (typeof d === "string" ? d : null);
      if (!msg) return null;
      return field ? `${field} : ${msg}` : msg;
    })
    .filter(Boolean);
  if (lines.length === 0) return null;
  return lines.slice(0, 3).join(" · ");
}

/**
 * Normalise n'importe quelle erreur en message string lisible.
 * @param {unknown} err
 * @param {string} [fallback]
 * @returns {string}
 */
export function normalizeError(err, fallback = GENERIC_FALLBACK) {
  if (err == null) return fallback;
  if (typeof err === "string") return err.trim() || fallback;

  // Cas Axios : objet avec `response`, `request`, `config`, `isAxiosError`…
  const status = err?.response?.status;
  const data = err?.response?.data;

  // 1. response.data.detail (FastAPI standard)
  if (data) {
    if (typeof data.detail === "string" && data.detail.trim()) return data.detail.trim();
    // FastAPI/Pydantic : detail peut être un tableau d'erreurs de validation
    const vd = _formatValidationErrors(data.detail);
    if (vd) return vd;
    // 2. response.data.message
    if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
    // 3. response.data.error (pattern fréquent)
    if (typeof data.error === "string" && data.error.trim()) return data.error.trim();
    // Cas où data est lui-même une string
    if (typeof data === "string" && data.trim()) return data.trim();
  }

  // 4. Statut HTTP connu → message standard
  if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];

  // 5. err.message (Error natif, AxiosError…) — mais on filtre les valeurs techniques peu lisibles
  if (typeof err.message === "string" && err.message.trim()) {
    const m = err.message.trim();
    // Filtre les messages bruyants
    if (/^Network Error$/i.test(m)) return "Connexion au serveur impossible. Vérifiez votre réseau.";
    if (/timeout/i.test(m)) return "Le serveur met trop de temps à répondre. Merci de réessayer.";
    if (/^Request failed with status code/i.test(m)) {
      // ex: "Request failed with status code 500" → message HTTP standard si dispo
      const code = parseInt(m.match(/\d+/)?.[0], 10);
      if (code && STATUS_MESSAGES[code]) return STATUS_MESSAGES[code];
    }
    return m;
  }

  // 6. Fallback générique
  return fallback;
}

/**
 * Affiche un toast d'erreur propre. Log le détail technique en console.
 * @param {unknown} err
 * @param {object} [opts]
 * @param {string} [opts.fallback]   Message générique de remplacement
 * @param {string} [opts.prefix]     Préfixe optionnel (ex: "Sauvegarde —")
 */
export function showError(err, opts = {}) {
  const { fallback = GENERIC_FALLBACK, prefix } = opts;
  const msg = normalizeError(err, fallback);
  const text = prefix ? `${prefix} ${msg}` : msg;
  try { toast.error(text); } catch { /* sonner pas monté */ }
  // Log technique pour debug
  try {
    const status = err?.response?.status;
    const data = err?.response?.data;
    // eslint-disable-next-line no-console
    console.error("[AITO.PF] Error", { status, message: msg, raw: err, data });
  } catch { /* ignore */ }
}
